import { mval } from './collect';
import type { Project, Collect, Master, MasterLp } from './types';

/** 오늘(YYYY-MM-DD) — 작성일 기본값. 예전엔 '2026-03-20' 고정이라 문서 작성일·임대기간이 틀렸다 */
function todayISO(): string { const d = new Date(); const z = (n: number) => String(n).padStart(2, '0'); return d.getFullYear() + '-' + z(d.getMonth() + 1) + '-' + z(d.getDate()); }

const FILE_RE = /\.(jpe?g|png|gif|webp|bmp|pdf|hwpx?|docx?|xlsx?|pptx?|zip|txt)$/i;
/** 수집값이 파일명(확장자)이면 빈 문자열 — 주민번호·주소 칸에 파일명 유입 방지 */
function textVal(v: string): string { return v && !FILE_RE.test(v) ? v : ''; }

export function addYears(iso: string, n: number): string {
  const m = (iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return (parseInt(m[1], 10) + n) + '-' + m[2] + '-' + m[3];
}

export function totShares(m: Master): number {
  const t = parseInt((m.total || '').replace(/[^0-9]/g, ''), 10) || 0;
  const u = parseInt((m.unit || '').replace(/[^0-9]/g, ''), 10) || 0;
  return u ? Math.round(t / u) : 0;
}

export function gpCoName(c: Collect): string {
  const co = c.cos.find((x) => x.rep === 'gp');
  return co ? (co.name || '') : '';
}

function blankMlp(name?: string): MasterLp {
  return { name: name || '', rrn: '', addr: '', amt: '', rel: '', tel: '' };
}

/** 출자금 미입력(빈칸) 시 출자총액을 GP+LP 좌단위 균등분할로 채움. 입력값은 보존, 나머지 좌는 GP 가산. */
function fillSplitAmts(m: Master): void {
  const onlyNum = (s?: string) => parseInt((s || '').replace(/[^0-9]/g, ''), 10) || 0;
  const unitN = onlyNum(m.unit) || 1000000;
  const totShr = unitN > 0 ? Math.floor(onlyNum(m.total) / unitN) : 0;
  const nMem = 1 + (Array.isArray(m.lps) ? m.lps.length : 0);
  const baseShr = nMem > 0 ? Math.floor(totShr / nMem) : 0;
  const remShr = totShr - baseShr * nMem;
  const fmt = (shr: number) => (shr * unitN).toLocaleString();
  const isEmpty = (v?: string) => v === undefined || String(v).replace(/[^0-9]/g, '') === '';
  if (isEmpty(m.gp.amt)) m.gp.amt = fmt(baseShr + remShr);
  (m.lps || []).forEach((l) => { if (isEmpty(l.amt)) l.amt = fmt(baseShr); });
}

function buildMaster(p: Project, c: Collect): Master {
  const gv = (k: string) => mval(c.gp, k);
  const gr = (c.gp.recips && c.gp.recips[0]) || { tel: '', email: '' };
  const rd = p.regDate || todayISO();
  const career = gv('gpCareer');
  return {
    org: p.org || '', addr: gv('orgAddr') || p.addr || '', zip: '',
    total: p.total || '', unit: p.per || '1,000,000', dur: p.dur || '5',
    gp: {
      name: p.gp || c.gp.name || '', rrn: textVal(gv('gpRrn')), addr: textVal(gv('gpAddr')), zip: '',
      edu: gv('gpEdu'), career, tel: gr.tel || p.gpTel || '', email: p.gpEmail || gr.email || '', amt: '',
    },
    lps: c.lps.map((l) => { const ml = blankMlp(l.name); ml.rrn = textVal(mval(l, 'lpRrn')); ml.addr = textVal(mval(l, 'lpAddr')); ml.tel = textVal(mval(l, 'lpPhone')) || (l.recips && l.recips[0] ? l.recips[0].tel : ''); return ml; }),
    edu: { name: '개인투자조합 업무집행조합원(GP) 양성 교육과정', org: '(사)한국엔젤투자협회장', period: rd + ' ~ ' + rd, date: rd },
    applyDate: rd, uniqueNo: '',
    s4: { owner: p.gp || c.gp.name || '', ownerRrn: textVal(gv('gpRrn')), ownerAddr: '', leasePeriod: rd + ' ~ ' + addYears(rd, 5), leaseDate: rd, openDate: rd, area: '' },
  };
}

/** 마스터 빈칸 목록 — 화면의 칸이 하나라도 비면 「필수값 완료」라고 하지 않는다
 *  (2026-10-23 지도사 「하나라도 빠지면 필수값 완료라고 하면 안 돼」 · GP 최종학력이 비었는데 완료로 보였음)
 *  빼는 칸: LP 소개자(없는 조합원이 많음) — 그 밖에 MasterFields 의 칸은 모두 센다(칸을 더하면 여기도 더한다) */
export function masterMissing(m: Master, opt: { s4?: boolean } = {}): string[] {
  const miss: string[] = [];
  const need = (v: unknown, label: string) => { if (!String(v ?? '').trim()) miss.push(label); };
  need(m.org, '조합명'); need(m.addr, '조합 소재지'); need(m.zip, '조합 우편번호');
  need(m.total, '출자금 총액'); need(m.unit, '1좌 금액'); need(m.dur, '존속기간');
  const gp = m.gp || ({} as Master['gp']);
  need(gp.amt, 'GP 출자금');
  need(gp.name, 'GP 성명'); need(gp.rrn, 'GP 주민등록번호'); need(gp.tel, 'GP 전화번호'); need(gp.email, 'GP 이메일');
  need(gp.addr, 'GP 집주소'); need(gp.zip, 'GP 집 우편번호'); need(gp.edu, 'GP 최종학력'); need(gp.career, 'GP 주요경력');
  (m.lps || []).forEach((l, i) => {
    const n = 'LP' + (i + 1) + ' ';
    need(l.name, n + '성명'); need(l.rrn, n + '주민등록번호'); need(l.addr, n + '주소'); need(l.amt, n + '출자금'); need(l.tel, n + '휴대폰번호');
  });
  const ed = m.edu || ({} as Master['edu']);
  need(ed.name, '교육명'); need(ed.org, '교육기관'); need(ed.period, '교육기간'); need(ed.date, '교육 이수일');
  // 다 적었어도 GP + LP 출자금 합이 출자총액과 다르면 완료가 아님(화면의 ⚠ 줄과 같은 셈)
  const won = (v: unknown) => Number(String(v ?? '').replace(/[^\d]/g, '')) || 0;
  const tot = won(m.total);
  const sum = won(gp.amt) + (m.lps || []).reduce((acc, l) => acc + won(l.amt), 0);
  if (tot > 0 && sum > 0 && sum !== tot) miss.push('출자 구성 합계(출자총액과 다름)');
  // 4단계 칸(조합 주소 소유주 · 임대차 · LP 관계) — 4단계 화면에서만 셈(다른 단계엔 칸이 안 보임 · 2026-10-23 지도사 「소유주 · 주민번호 · 휴대폰번호도 마찬가지」)
  //  빼는 칸: 조합 개업일(비면 신청일이 맞는 값)
  if (opt.s4) {
    const s4 = m.s4 || ({} as Master['s4']);
    need(s4.owner, '조합 주소 소유주'); need(s4.ownerRrn, '소유주 주민등록번호'); need(s4.ownerTel, '소유주 휴대폰번호'); need(s4.ownerAddr, '소유주 집주소');
    (s4.owners || []).slice(1).forEach((o, i) => {
      const n = '임대인 ' + (i + 2) + ' ';
      need(o.name, n + '이름'); need(o.rrn, n + '주민등록번호'); need(o.tel, n + '휴대폰번호'); need(o.addr, n + '주소');
    });
    need(s4.leasePeriod, '임대차 계약기간'); need(s4.leaseDate, '임대차계약서 작성일'); need(s4.area, '조합 주소지 면적');
    (m.lps || []).forEach((l, i) => need(l.rel, 'LP' + (i + 1) + ' 관계'));
  }
  return miss;
}

/** 시안 ensureMaster: 있으면 동기화(클론 반환), 없으면 신규 생성 */
export function ensuredMaster(p: Project, c: Collect): Master {
  if (p.master) {
    const m = JSON.parse(JSON.stringify(p.master)) as Master;
    m.lps = c.lps.map((l, i) => {
      const ml = m.lps[i] || blankMlp(l.name);
      if (!ml.name && l.name) ml.name = l.name;
      if (ml.rrn && FILE_RE.test(ml.rrn)) ml.rrn = '';
      if (ml.addr && FILE_RE.test(ml.addr)) ml.addr = '';
      if (!ml.rrn) ml.rrn = textVal(mval(l, 'lpRrn'));
      if (!ml.addr) ml.addr = textVal(mval(l, 'lpAddr'));
      if (!ml.tel) ml.tel = textVal(mval(l, 'lpPhone')) || (l.recips && l.recips[0] ? l.recips[0].tel : '');
      return ml;
    });
    m.lps.forEach((l) => {
      if (l.rel === undefined) l.rel = '';
      if (l.amt === undefined) l.amt = '';
      if (l.tel === undefined) l.tel = '';
    });
    // 최초 생성 후에도 등록정보·자료수집 최신값으로 '빈 칸만' 백필 (출자총액·소재지·GP 누락 방지)
    const gv = (k: string) => mval(c.gp, k);
    if (m.gp.rrn && FILE_RE.test(m.gp.rrn)) m.gp.rrn = '';   // 기존에 새어든 파일명 제거
    if (m.gp.addr && FILE_RE.test(m.gp.addr)) m.gp.addr = '';
    if (m.s4 && m.s4.ownerRrn && FILE_RE.test(m.s4.ownerRrn)) m.s4.ownerRrn = '';
    if (!m.org) m.org = p.org || '';
    if (!m.addr) m.addr = gv('orgAddr') || p.addr || '';
    if (!m.total) m.total = p.total || '';
    if (!m.unit) m.unit = p.per || '1,000,000';
    if (!m.dur) m.dur = p.dur || '5';
    if (!m.gp.name) m.gp.name = p.gp || c.gp.name || '';
    if (!m.gp.rrn) m.gp.rrn = textVal(gv('gpRrn'));
    if (!m.gp.addr) m.gp.addr = textVal(gv('gpAddr'));
    if (!m.gp.edu) m.gp.edu = gv('gpEdu');
    if (!m.gp.career) m.gp.career = gv('gpCareer');
    if (!m.gp.tel) m.gp.tel = (c.gp.recips && c.gp.recips[0] && c.gp.recips[0].tel) || p.gpTel || '';
    if (!m.gp.email) m.gp.email = p.gpEmail || (c.gp.recips && c.gp.recips[0] && c.gp.recips[0].email) || '';
    fillSplitAmts(m);
    if (!m.s4) {
      const rd2 = p.regDate || todayISO();
      m.s4 = { owner: m.gp.name || '', ownerRrn: m.gp.rrn || '', ownerAddr: '', leasePeriod: rd2 + ' ~ ' + addYears(rd2, 5), leaseDate: rd2, openDate: rd2, area: '' };
    }
    if (m.s4.ownerAddr === undefined) m.s4.ownerAddr = '';
    if (m.uniqueNo === undefined) m.uniqueNo = '';
    return m;
  }
  const nm = buildMaster(p, c); fillSplitAmts(nm); return nm;
}
